import * as React from 'react'
import { SVGIconName } from 'svgIcons'
import { useInView } from '../hooks/useInView'
import { cn } from '../utils/addToClassName'
import { prefersReducedMotion } from '../utils/mediaQueries'
import { translate } from '../utils/translate'
import { Clickable } from './clickable'
import { CroppedImageURLs, Img } from './img'
import { SVGIcon } from './svg'

type PageheaderUpdater = { isExternal: boolean; url: string; title: string }

// DEFAULT pageheader: will be created later in the redesign sprint

// export const Pageheader = ({
//   title,
//   introduction,
//   primaryUpdater,
//   image
// }: {
//   title?: string
//   introduction?: React.ReactNode
//   image?: { alt: string } & CroppedImageURLs
//   primaryUpdater?: PageheaderUpdater
// }) => {
//   return (
//     <header className={`pageheader ph ph--${image ? 'image' : 'text'}`}>
//       <div className="ph__inner">
//         <div className={`ph__text`}>
//           {title && <h1 className="ph__title" dangerouslySetInnerHTML={{ __html: title }} />}

//           {introduction &&
//             (typeof introduction === 'string' ? (
//               <p className="ph__intro" dangerouslySetInnerHTML={{ __html: introduction }} />
//             ) : (
//               <p className="ph__intro">{introduction}</p>
//             ))}

//           {primaryUpdater && (
//             <div className="ph__clickables ls-clickables mt-gutter-l">
//               {primaryUpdater && (
//                 <div className="ls-clickable">
//                   <Clickable elementKind="link" styleKind="button" {...primaryUpdater} className="btn btn-primary" />
//                 </div>
//               )}
//             </div>
//           )}
//         </div>

//         {image && (
//           <div className="ph__visual col-md-5 d-lg-block d-none">
//             <Img
//               {...image}
//               width="512"
//               height="384"
//               sizes="(min-width: 1280px) 512px, (min-width: 768px) 40vw, 0px"
//               className="ph__image bottom-0 h-100 position-absolute top-0"
//               loading="eager"
//             />
//           </div>
//         )}
//       </div>
//     </header>
//   )
// }

// Currently only used on the homepage, with a hardcoded video

export const VideoPageheader = ({
  title,
  primaryUpdater,
  poster,
  kpis,
  viewportWidth
}: {
  title?: string
  poster?: { alt: string } & CroppedImageURLs
  kpis?: {
    icon: string
    text: string
  }[]
  primaryUpdater?: PageheaderUpdater
  viewportWidth: number
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [isLoaded, setVideoLoaded] = React.useState(false)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const a11yPlayLabel = isPlaying ? translate('Header:A11y:PauseVideo') : translate('Header:A11y:PlayVideo')
  const { inView, ref } = useInView(
    { threshold: 0 },
    (inview) => {
      if (videoRef.current) !inview || prefersReducedMotion ? videoRef.current.pause() : videoRef.current.play()
    },
    true
  )

  const textRef = React.useRef<HTMLDivElement>(null)
  const innerRef = React.useRef<HTMLDivElement>(null)
  const bottomRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (textRef.current && innerRef.current && bottomRef.current) {
      innerRef.current.style.setProperty('--js-text-height', `${textRef.current.offsetHeight}px`)
    }
  }, [viewportWidth])

  return (
    <header ref={ref} className={cn('pageheader ph ph--video', inView ? 'inview' : 'not-inview')}>
      {/* ----- Video */}
      <div className="ph__visual">
        <video
          ref={videoRef}
          id="a11y-ph-video"
          preload="auto"
          src="https://giustoragev2.blob.core.windows.net/shoppinger/vanoord/videos/vanoord-home-video.mp4"
          className="ph__video"
          autoPlay={isLoaded && !prefersReducedMotion}
          loop
          muted
          playsInline
          onCanPlay={() => {
            setVideoLoaded(true)
            if (videoRef.current) fadeInVideo(videoRef.current)
          }}
          onPlaying={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        />

        {/* Duplicated Text structure (only visible on top of video, in white), hidden from screen-readers */}
        <div className="ph-duplicated ls-container" aria-hidden="true">
          <div className="ph__text">
            <div className="ph__text-inner">
              {title && <div className="ph__title title--xl">{title}</div>}

              {primaryUpdater && (
                <div className="ph__clickables ls-clickables">
                  <div className="ls-clickable">
                    <div
                      className="outline-button"
                      onClick={() => (window.location.href = primaryUpdater.url)}
                      style={{ cursor: 'pointer' }}
                      aria-hidden="true"
                      tabIndex={-1}
                    >
                      {primaryUpdater.title}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Poster */}
        <div className="ph__poster-wrapper" hidden={isLoaded}>
          <Img {...poster} width="1440" height="840" sizes="100vw" className="ph__poster" loading="eager" />
        </div>
      </div>

      {/* ----- Content */}
      <div ref={innerRef} className="ph__inner ls-container">
        {/* Text */}
        <div className="ph__text">
          <div ref={textRef} className="ph__text-inner">
            {title && <h1 className="ph__title title--xl">{title}</h1>}

            {primaryUpdater && (
              <div className="ph__clickables ls-clickables">
                <div className="ls-clickable">
                  <Clickable elementKind="link" styleKind="button" {...primaryUpdater} className="outline-button" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Play/pause button (only if prefersReducedMotion is enabled) */}
        {prefersReducedMotion && isLoaded && (
          <div
            className={cn(
              'ph__button-play-wrapper',
              isLoaded ? 'ph__button-play-wrapper--loaded' : 'ph__button-play-wrapper--loading'
            )}
          >
            <button
              type="button"
              className="ph__button-play"
              aria-controls="a11y-ph-video"
              aria-pressed={videoRef.current ? !videoRef.current.paused : false}
              aria-labelledby="a11y-ph-video-label"
              onClick={() => {
                if (videoRef.current) {
                  if (videoRef.current.paused) {
                    videoRef.current.play()
                  } else {
                    videoRef.current.pause()
                  }
                }
              }}
              title={a11yPlayLabel}
            >
              <span id="a11y-ph-video-label" className="sr-text">
                {a11yPlayLabel}
              </span>
              <SVGIcon id={isPlaying ? 'video-pause' : 'video-play'} />
            </button>
          </div>
        )}

        {/* KPIs */}
        {kpis && kpis.length > 0 && (
          <div ref={bottomRef} className="ph__bottom">
            <ul className="ph__kpi-list kpis">
              {kpis.map((kpi, index) => (
                <li key={index} className="kpi__item">
                  <SVGIcon
                    id={`project-${kpi.icon.replace('.svg', '')}` as SVGIconName}
                    size={120}
                    className="kpi__icon"
                  />
                  <p className="kpi__text">{kpi.text}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </header>
  )
}

const fadeInVideo = (videoEl: HTMLVideoElement) =>
  !prefersReducedMotion
    ? videoEl.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: 500
    })
    : undefined
